<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { salesTargetMethods } from "@/state/helpers";

export default {
  page: { title: i18n.t("label.listingX", [i18n.t("module.salesTarget")]) },
  components: { Layout, PageHeader },
  data() {
    return {
      title: i18n.t("label.listingX", [i18n.t("module.salesTarget")]),
      agents: [],
      loading: true,
      search: "",
      monthlyData: [],
      monthlyYear: new Date().getFullYear(),
    };
  },
  computed: {
    filteredAgents() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.agents;
      return this.agents.filter((a) =>
        (a.agent_code || "").toLowerCase().includes(q) ||
        (a.agent_name || "").toLowerCase().includes(q)
      );
    },
    totalAgents() { return this.agents.length; },
    agentsWithPlans() { return this.agents.filter((a) => a.plans_count > 0).length; },
    totalPlans() { return this.agents.reduce((s, a) => s + (a.plans_count || 0), 0); },
    grandTotal() { return this.agents.reduce((s, a) => s + Number(a.total_target || 0), 0); },
    topAgents() {
      return [...this.agents]
        .filter((a) => a.total_target > 0)
        .sort((a, b) => b.total_target - a.total_target)
        .slice(0, 8);
    },
    hasMonthlyData() {
      return this.monthlyData.some((m) => m.target > 0 || m.actual > 0);
    },
    monthlyAchievementPct() {
      const t = this.monthlyData.reduce((s, m) => s + Number(m.target || 0), 0);
      const a = this.monthlyData.reduce((s, m) => s + Number(m.actual || 0), 0);
      return t > 0 ? Math.round((a / t) * 1000) / 10 : 0;
    },
    barChartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "inherit", stacked: false },
        plotOptions: { bar: { borderRadius: 6, horizontal: false, columnWidth: "60%" } },
        dataLabels: { enabled: false },
        colors: ["#C9B8FF", "#7C4DFF"],
        legend: { position: "top", horizontalAlign: "right", fontSize: "11px", markers: { radius: 6 } },
        xaxis: {
          categories: this.monthlyData.map((m) => m.label),
          labels: { style: { fontSize: "11px" } },
        },
        yaxis: {
          labels: {
            formatter: (v) => `RM ${(v / 1000).toFixed(0)}k`,
            style: { fontSize: "11px" },
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: { formatter: (v) => `RM ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
        },
        grid: { borderColor: "#f1f3f5", strokeDashArray: 4 },
      };
    },
    barChartSeries() {
      return [
        { name: "Target", data: this.monthlyData.map((m) => m.target) },
        { name: "Achieved", data: this.monthlyData.map((m) => m.actual) },
      ];
    },
    donutLabels() {
      const top = this.topAgents.slice(0, 5);
      const labels = top.map((a) => a.agent_code);
      const others = this.agents
        .filter((a) => a.total_target > 0 && !top.includes(a))
        .reduce((s, a) => s + a.total_target, 0);
      if (others > 0) labels.push("Others");
      return labels;
    },
    donutSeries() {
      const top = this.topAgents.slice(0, 5);
      const series = top.map((a) => a.total_target);
      const others = this.agents
        .filter((a) => a.total_target > 0 && !top.includes(a))
        .reduce((s, a) => s + a.total_target, 0);
      if (others > 0) series.push(others);
      return series;
    },
    donutOptions() {
      return {
        chart: { fontFamily: "inherit" },
        labels: this.donutLabels,
        colors: ["#7C4DFF", "#9C6AFF", "#5E2FE0", "#B388FF", "#651FFF", "#8993A4"],
        legend: { position: "bottom", fontSize: "12px" },
        plotOptions: {
          pie: {
            donut: {
              size: "68%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "Total",
                  formatter: () => `RM ${(this.grandTotal / 1000).toFixed(0)}k`,
                },
                value: {
                  formatter: (v) => `RM ${(Number(v) / 1000).toFixed(0)}k`,
                },
              },
            },
          },
        },
        dataLabels: { enabled: false },
        tooltip: {
          y: { formatter: (v) => `RM ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
        },
      };
    },
  },
  async created() { await this.fetch(); },
  methods: {
    ...salesTargetMethods,
    async fetch() {
      this.loading = true;
      const [byAgentRes, monthlyRes] = await Promise.all([
        this.getByAgent(),
        this.getMonthlySummary({ year: this.monthlyYear }),
      ]);
      this.agents = byAgentRes?.data || [];
      this.monthlyData = monthlyRes?.data?.months || [];
      this.loading = false;
    },
    goAgent(row) {
      this.$router.push({ name: "Sales-target-agent-detail", params: { agentCode: row.agent_code } });
    },
    fmtRm(v) {
      const n = Number(v) || 0;
      return n.toLocaleString(undefined, { style: "currency", currency: "MYR", minimumFractionDigits: 2 });
    },
    fmtShort(v) {
      const n = Number(v) || 0;
      if (n >= 1000000) return `RM ${(n / 1000000).toFixed(1)}M`;
      if (n >= 1000) return `RM ${(n / 1000).toFixed(1)}k`;
      return `RM ${n.toFixed(0)}`;
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" />

    <!-- STAT CARDS -->
    <div class="row stat-row">
      <div class="col-6 col-md-3">
        <div class="stat-card stat-purple">
          <div class="stat-icon"><i class="mdi mdi-account-group-outline"></i></div>
          <div class="stat-body">
            <div class="stat-num">{{ totalAgents }}</div>
            <div class="stat-cap">{{ $t('salesTarget.listing.totalAgents') }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="stat-card stat-blue">
          <div class="stat-icon"><i class="mdi mdi-account-check-outline"></i></div>
          <div class="stat-body">
            <div class="stat-num">{{ agentsWithPlans }}</div>
            <div class="stat-cap">{{ $t('salesTarget.listing.withPlans') }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="stat-card stat-cyan">
          <div class="stat-icon"><i class="mdi mdi-target"></i></div>
          <div class="stat-body">
            <div class="stat-num">{{ totalPlans }}</div>
            <div class="stat-cap">{{ $t('salesTarget.listing.totalPlans') }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="stat-card stat-green">
          <div class="stat-icon"><i class="mdi mdi-cash-multiple"></i></div>
          <div class="stat-body">
            <div class="stat-num">{{ fmtShort(grandTotal) }}</div>
            <div class="stat-cap">{{ $t('salesTarget.listing.grandTotal') }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- CHARTS -->
    <div class="row" v-if="!loading && totalPlans > 0">
      <div class="col-12 col-lg-7">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="card-title-purple mb-0">
                <i class="mdi mdi-chart-bar"></i> {{ $t('salesTarget.listing.topAgentsChart') }} ({{ monthlyYear }})
              </h6>
              <span v-if="hasMonthlyData" class="text-muted small">
                {{ $t('salesTarget.listing.overall') }} <b class="text-purple">{{ monthlyAchievementPct }}%</b>
              </span>
            </div>
            <apexchart
              v-if="hasMonthlyData"
              type="bar"
              height="280"
              :options="barChartOptions"
              :series="barChartSeries"
            />
            <div v-else class="text-center py-5 text-muted">
              <i class="mdi mdi-chart-bar display-4"></i>
              <p class="mt-2 mb-0">No monthly target data yet for {{ monthlyYear }}.</p>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 col-lg-5">
        <div class="card">
          <div class="card-body">
            <h6 class="card-title-purple">
              <i class="mdi mdi-chart-donut"></i> {{ $t('salesTarget.listing.targetDistribution') }}
            </h6>
            <apexchart
              v-if="donutSeries.length"
              type="donut"
              height="280"
              :options="donutOptions"
              :series="donutSeries"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- AGENT GRID -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mt-2 mb-3 align-items-center">
              <div class="col-12 col-md-6">
                <b-input-group>
                  <b-input-group-prepend>
                    <span class="input-group-text"><i class="mdi mdi-magnify"></i></span>
                  </b-input-group-prepend>
                  <b-form-input v-model="search" :placeholder="$t('salesTarget.listing.searchPlaceholder')" />
                </b-input-group>
              </div>
            </div>

            <div v-if="loading" class="text-center py-5">
              <b-spinner small variant="primary"></b-spinner>
              <p class="text-muted mt-2 mb-0">{{ $t('salesTarget.listing.loadingAgents') }}</p>
            </div>

            <div v-else-if="!filteredAgents.length" class="text-center py-5">
              <i class="mdi mdi-account-search-outline display-4 text-muted"></i>
              <p class="text-muted mb-0">{{ $t('salesTarget.listing.noAgents') }}</p>
            </div>

            <div v-else class="agent-grid">
              <div
                v-for="a in filteredAgents"
                :key="a.agent_code"
                class="agent-card"
                @click="goAgent(a)"
              >
                <div class="agent-card-head">
                  <div class="agent-avatar">{{ (a.agent_name || a.agent_code || '?').charAt(0).toUpperCase() }}</div>
                  <div class="agent-meta">
                    <div class="agent-code">{{ a.agent_code }}</div>
                    <div class="agent-name">{{ a.agent_name || '—' }}</div>
                  </div>
                  <i class="mdi mdi-chevron-right agent-chevron"></i>
                </div>
                <div class="agent-card-stats">
                  <div class="stat">
                    <div class="stat-label">{{ $t('salesTarget.listing.plans') }}</div>
                    <div class="stat-value">{{ a.plans_count || 0 }}</div>
                  </div>
                  <div class="stat">
                    <div class="stat-label">{{ $t('salesTarget.listing.totalTarget') }}</div>
                    <div class="stat-value">{{ fmtRm(a.total_target) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.stat-row { margin-bottom: 8px; }
.stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  border: 1px solid #e9ecef;
  margin-bottom: 12px;
  position: relative;
  overflow: hidden;
}
.stat-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0; bottom: 0;
  width: 4px;
}
.stat-purple::before { background: #7C4DFF; }
.stat-blue::before { background: #3D5AFE; }
.stat-cyan::before { background: #00BCD4; }
.stat-green::before { background: #10B981; }
.stat-icon {
  width: 44px; height: 44px;
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 22px;
}
.stat-purple .stat-icon { background: rgba(124, 77, 255, 0.12); color: #7C4DFF; }
.stat-blue .stat-icon { background: rgba(61, 90, 254, 0.12); color: #3D5AFE; }
.stat-cyan .stat-icon { background: rgba(0, 188, 212, 0.12); color: #00BCD4; }
.stat-green .stat-icon { background: rgba(16, 185, 129, 0.12); color: #10B981; }
.stat-body { flex: 1; min-width: 0; }
.stat-num { font-size: 22px; font-weight: 700; color: #212529; line-height: 1.1; }
.stat-cap { font-size: 11px; color: #8993A4; text-transform: uppercase; letter-spacing: 0.4px; font-weight: 600; }

.card-title-purple {
  font-size: 13px;
  font-weight: 700;
  color: #7C4DFF;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 14px;
  display: flex; align-items: center; gap: 6px;
}
.card-title-purple i { font-size: 18px; }
.text-purple { color: #7C4DFF; }

.agent-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
}
.agent-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.agent-card:hover {
  border-color: rgba(124, 77, 255, 0.4);
  box-shadow: 0 6px 16px rgba(124, 77, 255, 0.12);
  transform: translateY(-1px);
}
.agent-card-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.agent-avatar {
  width: 38px; height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7C4DFF, #9C6AFF);
  color: #fff;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  font-size: 15px;
}
.agent-meta { flex: 1; min-width: 0; }
.agent-code { font-weight: 700; font-size: 13px; color: #495057; }
.agent-name { font-size: 12px; color: #8993A4; }
.agent-chevron { color: #8993A4; font-size: 22px; }
.agent-card-stats { display: flex; gap: 14px; }
.stat { flex: 1; }
.stat-label {
  font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px;
  color: #8993A4; font-weight: 600;
}
.stat-value { font-size: 14px; font-weight: 700; color: #212529; margin-top: 2px; }
</style>
